import { proxy } from "valtio";
import log from "logbeacon";
import { sessionMap } from "./sessionMap";

/** 定义每个 key 对应的值类型 */
type SessionValueMap = {
  [sessionMap.REFERRAL_AUTO_LOGIN]: boolean;
  [sessionMap.LOGIN_GUIDE_DISMISSED]: boolean;
  // 添加新的 key 时在这里定义它的值类型
};

type SessionKey = keyof typeof sessionMap;
type SessionData = { [K in SessionKey]?: SessionValueMap[K] };

interface SessionManager {
  data: SessionData;
  setItem: <K extends SessionKey>(key: K, value: SessionValueMap[K]) => void;
  getItem: <K extends SessionKey>(key: K) => SessionValueMap[K] | null;
  removeItem: (key: SessionKey) => void;
  clear: () => void;
}

/**
|--------------------------------------------------
| SessionManager
| 1.在当前浏览器会话中保持数据
| 2.刷新页面时保留数据
| 3.新开浏览器时数据不存在
| 4.保持了 valtio 的响应式特性
|--------------------------------------------------
*/
const createSessionManager = () => {
  const store = proxy<SessionManager>({
    data: {},

    setItem<K extends SessionKey>(key: K, value: SessionValueMap[K]) {
      try {
        const serialized = JSON.stringify(value);
        this.data[key] = value;
        sessionStorage.setItem(sessionMap[key], serialized);
      } catch (err) {
        log.error("[文案]存储到 session storage 失败:", err);
      }
    },

    getItem<K extends SessionKey>(key: K): SessionValueMap[K] | null {
      try {
        if (this.data[key] !== undefined) {
          return this.data[key] as SessionValueMap[K];
        }
        const value = sessionStorage.getItem(sessionMap[key]);
        if (value) {
          const parsed = JSON.parse(value);
          this.data[key] = parsed;
          return parsed;
        }
      } catch (err) {
        log.error("[文案]从 session storage 读取失败:", err);
      }
      return null;
    },

    removeItem(key: SessionKey) {
      try {
        delete this.data[key];
        sessionStorage.removeItem(sessionMap[key]);
      } catch (err) {
        log.error("[文案]从 session storage 删除失败:", err);
      }
    },

    clear() {
      try {
        this.data = {};
        sessionStorage.clear();
      } catch (err) {
        log.error("[文案]清除 session storage 失败:", err);
      }
    },
  });

  if (typeof window !== "undefined") {
    try {
      // 初始化时加载 sessionStorage 中的数据
      Object.keys(sessionMap).forEach((key) => {
        const value = sessionStorage.getItem(sessionMap[key as SessionKey]);
        if (value) {
          const parsed = JSON.parse(value);
          store.data[key as SessionKey] = parsed;
        }
      });
    } catch (err) {
      log.error("[文案]初始化 session storage 失败:", err);
    }
  }

  return store;
};

export const sessionManager = createSessionManager();

export const useSessionStorage = <K extends SessionKey>(
  key: K,
  initialValue: SessionValueMap[K],
) => {
  if (typeof window === "undefined") {
    return {
      getValue: () => initialValue,
      setValue: () => {},
      remove: () => {},
    };
  }
  if (sessionManager.getItem(key) === null && initialValue !== undefined) {
    sessionManager.setItem(key, initialValue);
  }

  return {
    getValue: () => sessionManager.getItem<K>(key),
    setValue: (value: SessionValueMap[K]) => sessionManager.setItem(key, value),
    remove: () => sessionManager.removeItem(key),
  };
};
